import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { apiGet } from '../../utils/api';

// Embedded Checkout normally completes inside the modal and the user never
// lands here. Stripe still requires a return_url, and 3DS / SCA challenges
// can force a top-level redirect mid-payment — this page handles that path.
//
// Confirms the session by calling the backend (which verifies the session
// belongs to the current user) and routes to billing with a success/error
// state. Never trusts the URL alone.
export default function CheckoutReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [state, setState] = useState({ phase: 'checking', error: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: 'error', error: 'Missing session reference.' });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await apiGet(`/billing/session/${encodeURIComponent(sessionId)}`);
        if (cancelled) return;

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setState({ phase: 'error', error: body.error || 'Could not verify your payment.' });
          return;
        }

        const data = await res.json();
        if (data.status === 'complete' && data.payment_status === 'paid') {
          // Refetch subscriptions so the dashboard reflects the new plan immediately.
          queryClient.invalidateQueries({ queryKey: ['billing', 'subscriptions'] });
          setState({ phase: 'success', error: null });
        } else if (data.status === 'open') {
          setState({ phase: 'incomplete', error: 'Your payment was not completed.' });
        } else {
          setState({ phase: 'error', error: 'Payment was not successful.' });
        }
      } catch (err) {
        if (!cancelled) setState({ phase: 'error', error: err.message });
      }
    })();

    return () => { cancelled = true; };
  }, [sessionId, queryClient]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
        {state.phase === 'checking' && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-blue-600" />
            <h1 className="mt-4 text-xl font-semibold text-gray-900 dark:text-gray-100">
              Confirming your payment…
            </h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Hold tight while we verify with Stripe.
            </p>
          </>
        )}

        {state.phase === 'success' && (
          <>
            <CheckCircle2 className="mx-auto h-10 w-10 text-green-600" />
            <h1 className="mt-4 text-xl font-semibold text-gray-900 dark:text-gray-100">
              You're in.
            </h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Your subscription is active. Welcome aboard.
            </p>
            <button
              onClick={() => navigate('/dashboard/billing')}
              className="mt-6 rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              Go to billing
            </button>
          </>
        )}

        {(state.phase === 'incomplete' || state.phase === 'error') && (
          <>
            <AlertCircle className="mx-auto h-10 w-10 text-red-600" />
            <h1 className="mt-4 text-xl font-semibold text-gray-900 dark:text-gray-100">
              {state.phase === 'incomplete' ? 'Payment incomplete' : 'We hit a snag'}
            </h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {state.error || 'Please try again.'}
            </p>
            <button
              onClick={() => navigate('/dashboard/billing/upgrade')}
              className="mt-6 rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              Try again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
